import React, { useState, useRef, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * BrandCombobox - Componente de autocomplete para seleção de marca de veículo.
 * Resiliente a mudanças rápidas na lista filtrada.
 * 
 * Props:
 * - brands: string[] - Lista de marcas disponíveis
 * - value: string - Marca selecionada
 * - onChange: (brand: string) => void - Callback quando marca é selecionada
 * - loading?: boolean - Se está carregando marcas
 * - disabled?: boolean - Se está desabilitado
 * - placeholder?: string - Placeholder do input
 * - testId?: string - data-testid para testes
 */
export default function BrandCombobox({
  brands = [],
  value,
  onChange,
  loading = false,
  disabled = false,
  placeholder = "Selecione a marca...",
  testId = "brand-combobox"
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef({});

  // Filtra marcas baseado na busca
  const filteredBrands = search
    ? brands.filter(b => b.toLowerCase().includes(search.toLowerCase()))
    : brands;

  // Reset highlight quando lista filtrada muda
  useEffect(() => {
    if (highlightedIndex >= filteredBrands.length) {
      setHighlightedIndex(Math.max(0, filteredBrands.length - 1));
    }
  }, [filteredBrands.length, highlightedIndex]);

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll para item destacado (com validação de existência)
  useEffect(() => {
    if (open && filteredBrands.length > 0) {
      const highlightedBrand = filteredBrands[highlightedIndex];
      if (highlightedBrand) {
        const itemElement = itemRefs.current[highlightedBrand];
        if (itemElement && typeof itemElement.scrollIntoView === 'function') {
          try {
            itemElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          } catch (e) {
            // Ignora erros de scrollIntoView
          }
        }
      }
    }
  }, [highlightedIndex, open, filteredBrands]);

  const handleSelect = (brand) => {
    onChange(brand);
    setSearch('');
    setOpen(false);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredBrands.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        const selectedBrand = filteredBrands[highlightedIndex];
        if (selectedBrand) {
          handleSelect(selectedBrand);
        }
        break;
      case 'Escape':
        setOpen(false);
        setSearch('');
        break;
      default:
        break;
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearch(newValue);
    setHighlightedIndex(0); // Reset highlight ao digitar
    if (!open) {
      setOpen(true);
    }
  };

  const handleTriggerClick = () => {
    if (!disabled) {
      setOpen(!open);
      if (!open) {
        // Focus no input quando abre
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      }
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid={testId}>
      {/* Trigger Button */}
      <Button
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled || loading}
        className={cn(
          "w-full justify-between font-normal",
          !value && "text-muted-foreground"
        )}
        onClick={handleTriggerClick}
        data-testid={`${testId}-trigger`}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Carregando...
          </span>
        ) : (
          value || placeholder
        )}
        <ChevronDown className={cn(
          "ml-2 h-4 w-4 shrink-0 opacity-50 transition-transform",
          open && "rotate-180"
        )} />
      </Button>

      {/* Dropdown */}
      {open && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b">
            <Input
              ref={inputRef}
              placeholder="Buscar marca..."
              value={search}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="h-8"
              autoFocus
              data-testid={`${testId}-search`}
            />
          </div>
          
          {/* Lista de Marcas */}
          <div 
            ref={listRef}
            className="max-h-[200px] overflow-auto p-1"
            role="listbox"
          >
            {filteredBrands.length === 0 ? (
              <div className="py-2 px-3 text-sm text-muted-foreground text-center">
                Nenhuma marca encontrada
              </div>
            ) : (
              filteredBrands.map((brand, index) => (
                <button
                  key={brand}
                  ref={el => { itemRefs.current[brand] = el; }}
                  type="button"
                  role="option"
                  aria-selected={value === brand}
                  className={cn(
                    "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none",
                    "hover:bg-accent hover:text-accent-foreground",
                    index === highlightedIndex && "bg-accent text-accent-foreground",
                    value === brand && "font-medium"
                  )}
                  onClick={() => handleSelect(brand)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  data-testid={`${testId}-option-${brand}`}
                >
                  {brand}
                  {value === brand && (
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Check className="h-4 w-4" />
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
